import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { routeTree } from './routeTree.gen';
import { queryClient } from './lib/query-client';
import { config } from './config/env.config';
import { initWebSentry } from './observability/sentry';
import { BrandingLoader } from './components/BrandingLoader';
import { startVersionCheck } from './lib/version-check';
import './index.css';

// Após um deploy, abas antigas pedem chunks de rota que não existem mais.
// Recarrega uma vez para buscar o index.html novo (com trava contra loop).
window.addEventListener('vite:preloadError', (event) => {
  const key = 'chunk-reload-at';
  try {
    const last = Number(sessionStorage.getItem(key) ?? 0);
    if (Date.now() - last < 30_000) return;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    return;
  }
  event.preventDefault();
  window.location.reload();
});

const router = createRouter({ routeTree });
initWebSentry(
  {
    app: 'asseto-front',
    dsn: config.sentry.dsn,
    environment: config.sentry.environment,
    release: config.sentry.release,
    apiBaseUrl: config.api.baseUrl,
    tracesSampleRate: config.sentry.tracesSampleRate,
    replaysSessionSampleRate: config.sentry.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.sentry.replaysOnErrorSampleRate,
  },
  { router },
);

startVersionCheck(router);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const app = (
  <BrandingLoader>
    <RouterProvider router={router} />
    {config.app.enableDevtools && <ReactQueryDevtools initialIsOpen={false} />}
  </BrandingLoader>
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {config.google.clientId ? (
        <GoogleOAuthProvider clientId={config.google.clientId}>{app}</GoogleOAuthProvider>
      ) : (
        app
      )}
    </QueryClientProvider>
  </React.StrictMode>,
);
